import { FitBoundsOptions, default as L, LatLngTuple } from 'leaflet';
import PrimeConfig from 'primevue/config';
import type { Component, MaybeRefOrGetter, Ref, ShallowRef } from 'vue';
import { createApp, markRaw, ref, toRef, toValue } from 'vue';

import {
  RygenUiPrimeVueConfig,
  rygenUiPrimeVueConfiguration,
} from '@rygen/rygen-ui/config/PrimeVueConfig';
import { Coordinates, Optional } from '@rygen/rygen-ui/model';

import i18n from '@/i18n';
import { GEOGRAPHIC_CENTER_OF_US, MapRenderMethod } from '@/model';

import { useMapTileService } from './useMapTileService';

type CreateAppReturnType = ReturnType<typeof createApp>;
type CreateAppMountMethod = CreateAppReturnType['mount'];
type MountParameters = Parameters<CreateAppMountMethod>;

export const MAP_RENDER_METHOD_CLASSES = {
  [MapRenderMethod.GEOJSON]: 'geojson',
  [MapRenderMethod.LEGACY]: 'legacy',
};

interface UseMapDetailsOptions {
  initialMapRenderMethod?: MaybeRefOrGetter<MapRenderMethod>;
}

const DEFAULT_OPTIONS: Required<UseMapDetailsOptions> = {
  initialMapRenderMethod: MapRenderMethod.GEOJSON,
};

interface UseMapDetailsResponse {
  centerMapOnAmerica: () => void;
  fitBoundsOptions: FitBoundsOptions;
  generateEventIcon: (classNames: string) => L.DivIcon;
  generateElementFromComponent: (
    component: Component,
    rootContainer: MountParameters[0],
  ) => HTMLElement;
  generateHtmlFromComponent: (
    component: Component,
    rootContainer: MountParameters[0],
  ) => string;
  generateStopIcon: (classNames: string) => L.DivIcon;
  initMap: (mapId: string) => L.Map;
  isDynamicMapLoading: Ref<boolean>;
  mapRenderMethod: Ref<MapRenderMethod>;
  mapRenderMethodClass: Readonly<Ref<string>>;
  toLatLngTuple: (coords: Optional<Coordinates>) => LatLngTuple;
  toOptionalLatLngTuple: (
    coords: Optional<Coordinates>,
  ) => Optional<LatLngTuple>;
}

/**
 * Logic for mapping
 *
 * Intended to be used by the map component, not the consumer (see `useMap`
 * for that).
 */
export const useMapDetails = (
  map: ShallowRef<Optional<L.Map>>,
  options = DEFAULT_OPTIONS,
): UseMapDetailsResponse => {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  const { accessToken, attribution, mapTileUrl } = useMapTileService();

  const fitBoundsOptions: FitBoundsOptions = {
    padding: [12, 12],
  };

  const mapRenderMethod: Ref<MapRenderMethod> = ref(
    toValue(opts.initialMapRenderMethod),
  );
  const isDynamicMapLoading = ref(false);

  const mapRenderMethodClass = toRef(
    () => MAP_RENDER_METHOD_CLASSES[mapRenderMethod.value],
  );

  const generateElementFromComponent = (
    component: Component,
    rootContainer: MountParameters[0],
  ): HTMLElement => {
    const fakeApp = createApp(component)
      .use(PrimeConfig, rygenUiPrimeVueConfiguration)
      .use(RygenUiPrimeVueConfig, rygenUiPrimeVueConfiguration)
      .use(i18n);

    const el = fakeApp.mount(rootContainer).$el as HTMLElement;
    fakeApp.unmount();

    return el;
  };

  const generateHtmlFromComponent = (
    component: Component,
    rootContainer: MountParameters[0],
  ) => {
    const fakeApp = createApp(component)
      .use(PrimeConfig, rygenUiPrimeVueConfiguration)
      .use(RygenUiPrimeVueConfig, rygenUiPrimeVueConfiguration)
      .use(i18n);

    const html: string = fakeApp.mount(rootContainer).$el.outerHTML;
    fakeApp.unmount();

    return html;
  };

  const toOptionalLatLngTuple = (
    coords: Optional<Coordinates>,
  ): Optional<LatLngTuple> => {
    if (!coords?.latitude || !coords?.longitude) {
      return;
    }

    return [coords.latitude, coords.longitude];
  };

  const toLatLngTuple = (coords: Optional<Coordinates>): LatLngTuple => {
    return toOptionalLatLngTuple(coords) ?? GEOGRAPHIC_CENTER_OF_US;
  };

  const generateStopIcon = (classNames: string) => {
    const isOD =
      classNames.includes('origin') || classNames.includes('destination');
    return L.divIcon({
      html: '<i class="pi pi-map-marker"></i>',
      iconSize: isOD ? [28, 28] : [16, 16],
      iconAnchor: isOD ? [14, 28] : [8, 16],
      popupAnchor: [0, -25],
      className: classNames,
    });
  };

  const generateEventIcon = (classNames: string) => {
    return L.divIcon({
      html: '<i class="pi pi-circle-fill"></i>',
      iconSize: [20, 50],
      iconAnchor: [10, 10],
      className: classNames,
    });
  };

  const initMap = (mapId: string) => {
    isDynamicMapLoading.value = true;

    map.value?.remove();
    map.value = markRaw(
      L.map(mapId, {
        center: GEOGRAPHIC_CENTER_OF_US,
        dragging: true,
        scrollWheelZoom: true,
        doubleClickZoom: true,
        zoomControl: false,
        zoomSnap: 0.5,
        attributionControl: false,
      }),
    );

    L.tileLayer(mapTileUrl.value, {
      maxZoom: 18,
      tileSize: 512,
      zoomOffset: -1,
      // @ts-expect-error TS2322: @types/leaflet claims this is not necessary, will re-evaluate in the future
      accessToken: accessToken.value,
      attribution: attribution.value,
    }).addTo(map.value);

    map.value.on('load', () => {
      isDynamicMapLoading.value = false;
    });

    return map.value;
  };

  const centerMapOnAmerica = () => {
    map.value?.setView(GEOGRAPHIC_CENTER_OF_US, 3.5);
  };

  return {
    centerMapOnAmerica,
    fitBoundsOptions,
    generateEventIcon,
    generateElementFromComponent,
    generateHtmlFromComponent,
    generateStopIcon,
    initMap,
    isDynamicMapLoading,
    mapRenderMethod,
    mapRenderMethodClass,
    toLatLngTuple,
    toOptionalLatLngTuple,
  };
};
